<template>
  <div class="performance-benchmarking">
    <!-- Navigation Header -->
    <div class="analytics-nav-header">
      <div class="breadcrumb">
        <button class="breadcrumb-item" @click="navigateBack">
          <i class="bi bi-speedometer2"></i>
          Analytics Dashboard
        </button>
        <i class="bi bi-chevron-right breadcrumb-separator"></i>
        <span class="breadcrumb-current">Performance Benchmarking</span>
      </div>
      <button class="back-button" @click="navigateBack">
        <i class="bi bi-arrow-left"></i>
        Back to Dashboard
      </button>
    </div>

    <div class="section-header">
      <h3>Performance Benchmarking</h3>
      <p>
        Analyze rating distributions, scout accuracy, and success predictions
      </p>
    </div>

    <!-- Benchmark Summary Cards -->
    <div class="benchmark-cards">
      <div class="benchmark-card">
        <div class="benchmark-icon excellent">
          <i class="bi bi-bullseye"></i>
        </div>
        <div class="benchmark-content">
          <h4>{{ overallAccuracy }}%</h4>
          <p>Overall Prediction Accuracy</p>
          <span class="benchmark-trend trend-positive">
            +{{ accuracyTrend }}% vs last period
          </span>
        </div>
      </div>

      <div class="benchmark-card">
        <div class="benchmark-icon good">
          <i class="bi bi-trophy"></i>
        </div>
        <div class="benchmark-content">
          <h4>{{ bestCategory }}</h4>
          <p>Most Predictive Category</p>
          <span class="benchmark-detail">
            {{ bestCategoryAccuracy }}% accuracy
          </span>
        </div>
      </div>

      <div class="benchmark-card">
        <div class="benchmark-icon warning">
          <i class="bi bi-graph-up"></i>
        </div>
        <div class="benchmark-content">
          <h4>{{ averageRating }}</h4>
          <p>Average Initial Rating</p>
          <span class="benchmark-detail">
            σ = {{ ratingVariance }} standard deviation
          </span>
        </div>
      </div>

      <div class="benchmark-card">
        <div class="benchmark-icon info">
          <i class="bi bi-people"></i>
        </div>
        <div class="benchmark-content">
          <h4>{{ sampleSize }}</h4>
          <p>Number of data points/records used</p>
          <span class="benchmark-detail">
            Across all time
          </span>
        </div>
      </div>
    </div>

    <div class="analytics-grid">
      <!-- Rating Distributions -->
      <div class="chart-container full-width">
        <div class="chart-header">
          <h4>Rating Distribution Analysis</h4>
          <p class="chart-subtitle">
            Statistical distribution by performance category
          </p>
          <div class="chart-controls">
            <select v-model="selectedCategory" @change="handleCategoryChange" class="form-select">
              <option value="all">All Categories</option>
              <option value="technical">Technical</option>
              <option value="physical">Physical</option>
              <option value="mental">Mental</option>
              <option value="overall">Overall</option>
            </select>
          </div>
        </div>
        <div class="chart-placeholder">
          <canvas ref="distributionChart" width="800" height="400"></canvas>
        </div>
      </div>

      <!-- Success Prediction Model -->
      <div class="chart-container">
        <div class="chart-header">
          <h4>Success Prediction Accuracy</h4>
          <p class="chart-subtitle">Prediction model performance by category</p>
        </div>
        <div class="prediction-metrics">
          <div v-for="metric in predictionMetrics" :key="metric.category" class="metric-item">
            <div class="metric-header">
              <span class="metric-name">{{ metric.category }}</span>
              <span class="metric-score" :class="getAccuracyClass(metric.accuracy)">
                {{ metric.accuracy }}%
              </span>
            </div>
            <div class="metric-bar">
              <div class="metric-fill" :class="getAccuracyClass(metric.accuracy)"
                :style="{ width: metric.accuracy + '%' }"></div>
            </div>
            <div class="metric-details">
              <span>{{ metric.sampleSize }} predictions</span>
              <span class="confidence">±{{ metric.confidence }}% confidence</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";

export default {
  name: "PerformanceBenchmarking",
  props: {
    analyticsData: {
      type: Object,
      default: () => ({}),
    },
    dateRange: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    // Assessment data for real calculations
    allAssessmentsData: {
      type: Array,
      default: () => [],
    },
    signedTrialistsData: {
      type: Array,
      default: () => [],
    },
    gameAttendanceData: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["data-point-click", "navigate-back"],
  data() {
    return {
      distributionChart: null,
      selectedCategory: "all",
      _updating: false, // Flag to prevent recursive updates
      _chartUpdateTimeout: null, // Debounce chart updates

      // Summary data - calculated from real assessment data
      overallAccuracy: 0,
      accuracyTrend: 0,
      bestCategory: "",
      bestCategoryAccuracy: 0,
      averageRating: 0,
      ratingVariance: 0,
      sampleSize: 0,

      // Prediction metrics - will be calculated from real assessment data
      predictionMetrics: [
        {
          category: "Technical Rating",
          accuracy: 0,
          sampleSize: 0,
          confidence: 0,
        },
        {
          category: "Physical Rating",
          accuracy: 0,
          sampleSize: 0,
          confidence: 0,
        },
        {
          category: "Mental Rating",
          accuracy: 0,
          sampleSize: 0,
          confidence: 0,
        },
        {
          category: "Overall Rating",
          accuracy: 0,
          sampleSize: 0,
          confidence: 0,
        },
      ],
    };
  },
  computed: {
    distributionData() {
      // Calculate distribution data from real assessment data
      const categories = this.calculateCategoryDistributions();
      const category = categories[this.selectedCategory] || categories.all;
      return this.generateNormalDistribution(
        category.mean,
        category.stdDev,
        category.label,
      );
    },

  },
  watch: {
    selectedCategory: {
      handler(newVal, oldVal) {
        console.log("Watcher: Category changed from", oldVal, "to", newVal);
        if (newVal !== oldVal) {
          this.updateDistributionChart();
        }
      },
      immediate: false,
    },
    // Recalculate metrics when assessment data changes
    allAssessmentsData() {
      this.calculatePerformanceMetrics();
    },
    signedTrialistsData() {
      this.calculatePerformanceMetrics();
    },
    gameAttendanceData() {
      this.calculatePerformanceMetrics();
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.calculatePerformanceMetrics(); // Calculate real metrics first
      this.createDistributionChart();
    });
  },
  beforeUnmount() {
    // Clear any pending updates
    if (this._chartUpdateTimeout) {
      clearTimeout(this._chartUpdateTimeout);
    }

    // Halt animations before destroy so the animator doesn't tick into a
    // null canvas after the DOM node is gone (Chart.js clearCanvas crash).
    if (this.distributionChart) {
      try { this.distributionChart.stop(); } catch (e) { /* noop */ }
      this.distributionChart.destroy();
      this.distributionChart = null;
    }
  },
  methods: {
    // Calculate performance metrics from real assessment data
    calculatePerformanceMetrics() {
      if (!this.allAssessmentsData.length) {
        // Set default values when no data available
        this.overallAccuracy = 0;
        this.accuracyTrend = 0;
        this.bestCategory = "N/A";
        this.bestCategoryAccuracy = 0;
        this.averageRating = 0;
        this.ratingVariance = 0;
        this.sampleSize = 0;
        this.updatePredictionMetrics([]);
        return;
      }

      // Sample size is total assessments
      this.sampleSize = this.allAssessmentsData.length;

      // Calculate category averages and find best category
      const categories = {
        Attitude: "attitude",
        Skill: "skill",
        Physical: "physique",
        Mental: "mentality",
        Leadership: "leadership",
        Professional: "professionalism",
      };

      const categoryStats = {};
      let totalRating = 0;
      let totalCount = 0;
      const allRatings = [];

      // Calculate stats for each category
      Object.keys(categories).forEach((categoryName) => {
        const fieldName = categories[categoryName];
        let categorySum = 0;
        let categoryCount = 0;
        const categoryRatings = [];

        this.allAssessmentsData.forEach((assessment) => {
          if (assessment[fieldName] && !isNaN(assessment[fieldName])) {
            const rating = parseFloat(assessment[fieldName]);
            categorySum += rating;
            categoryCount++;
            categoryRatings.push(rating);
            allRatings.push(rating);
          }
        });

        if (categoryCount > 0) {
          const average = categorySum / categoryCount;
          categoryStats[categoryName] = {
            average: average,
            count: categoryCount,
            ratings: categoryRatings,
          };
          totalRating += categorySum;
          totalCount += categoryCount;
        }
      });

      // Overall average rating
      this.averageRating =
        totalCount > 0 ? (totalRating / totalCount).toFixed(1) : 0;

      // Calculate rating variance (standard deviation)
      if (allRatings.length > 1) {
        const mean = totalRating / totalCount;
        const squaredDiffs = allRatings.map((rating) =>
          Math.pow(rating - mean, 2),
        );
        const avgSquaredDiff =
          squaredDiffs.reduce((sum, diff) => sum + diff, 0) / allRatings.length;
        this.ratingVariance = Math.sqrt(avgSquaredDiff).toFixed(1);
      } else {
        this.ratingVariance = 0;
      }

      // Find best category (highest average)
      let bestCategoryName = "N/A";
      let bestCategoryScore = 0;
      Object.keys(categoryStats).forEach((category) => {
        if (categoryStats[category].average > bestCategoryScore) {
          bestCategoryScore = categoryStats[category].average;
          bestCategoryName = category;
        }
      });
      this.bestCategory = bestCategoryName;
      this.bestCategoryAccuracy = bestCategoryScore.toFixed(1);

      // Calculate overall accuracy based on success predictions
      // Consider assessments with average rating >= 7 as "predicted successes"
      const predictedSuccesses = this.allAssessmentsData.filter(
        (assessment) => {
          const avgRating = this.calculateAssessmentAverageRating(assessment);
          return avgRating >= 7;
        },
      ).length;

      // Actual successes are signed players
      const actualSuccesses = this.signedTrialistsData.length;

      // Overall accuracy = (correct predictions / total predictions) * 100
      // Simplified: successful signings / predicted successes
      this.overallAccuracy =
        predictedSuccesses > 0
          ? ((actualSuccesses / predictedSuccesses) * 100).toFixed(1)
          : 0;

      // Calculate accuracy trend (simplified season comparison)
      this.accuracyTrend = this.calculateAccuracyTrend();

      // Update prediction metrics for each category
      this.updatePredictionMetrics(categoryStats);
    },

    // Helper method to calculate average rating for a single assessment
    calculateAssessmentAverageRating(assessment) {
      const categories = [
        "attitude",
        "skill",
        "physique",
        "mentality",
        "leadership",
        "professionalism",
      ];
      let total = 0;
      let count = 0;

      categories.forEach((category) => {
        if (assessment[category] && !isNaN(assessment[category])) {
          total += parseFloat(assessment[category]);
          count++;
        }
      });

      return count > 0 ? total / count : 0;
    },

    // Calculate accuracy trend over time
    calculateAccuracyTrend() {
      if (!this.gameAttendanceData.length || !this.allAssessmentsData.length)
        return 0;

      const currentDate = new Date();
      const threeMonthsAgo = new Date(
        currentDate.getTime() - 90 * 24 * 60 * 60 * 1000,
      );
      const sixMonthsAgo = new Date(
        currentDate.getTime() - 180 * 24 * 60 * 60 * 1000,
      );

      // Get assessments for recent 3 months vs previous 3 months
      let recentAccuracy = 0;
      let previousAccuracy = 0;

      const recentGames = this.gameAttendanceData.filter(
        (game) => new Date(game.matchDate) >= threeMonthsAgo,
      );

      const previousGames = this.gameAttendanceData.filter((game) => {
        const gameDate = new Date(game.matchDate);
        return gameDate >= sixMonthsAgo && gameDate < threeMonthsAgo;
      });

      // Calculate accuracy for recent period
      if (recentGames.length > 0) {
        const recentAssessments = this.allAssessmentsData.filter((assessment) =>
          recentGames.some(
            (game) => game.id === assessment.scoutAttendanceReportId,
          ),
        );
        recentAccuracy = this.calculatePeriodAccuracy(recentAssessments);
      }

      // Calculate accuracy for previous period
      if (previousGames.length > 0) {
        const previousAssessments = this.allAssessmentsData.filter(
          (assessment) =>
            previousGames.some(
              (game) => game.id === assessment.scoutAttendanceReportId,
            ),
        );
        previousAccuracy = this.calculatePeriodAccuracy(previousAssessments);
      }

      // Return trend percentage
      if (previousAccuracy === 0) return recentAccuracy > 0 ? 100 : 0;
      return (
        ((recentAccuracy - previousAccuracy) / previousAccuracy) *
        100
      ).toFixed(1);
    },

    // Calculate accuracy for a specific period — for each assessment that
    // predicted success (avg rating >= 7), count it as "actually successful"
    // only if the assessed player ended up being signed. The 30% hardcoded
    // baseline that lived here previously made every trend value fictional.
    calculatePeriodAccuracy(assessments) {
      if (!assessments.length) return 0;

      const signedNames = new Set(
        (this.signedTrialistsData || []).map((p) =>
          (p.name || "").trim().toLowerCase(),
        ),
      );

      const predicted = assessments.filter((assessment) => {
        const avgRating = this.calculateAssessmentAverageRating(assessment);
        return avgRating >= 7;
      });

      if (predicted.length === 0) return 0;

      const actualSuccesses = predicted.filter((a) =>
        signedNames.has((a.name || "").trim().toLowerCase()),
      ).length;

      return (actualSuccesses / predicted.length) * 100;
    },

    // Update prediction metrics based on real data
    updatePredictionMetrics(categoryStats) {
      const defaultMetrics = [
        {
          category: "Technical Rating",
          accuracy: 0,
          sampleSize: 0,
          confidence: 0,
        },
        {
          category: "Physical Rating",
          accuracy: 0,
          sampleSize: 0,
          confidence: 0,
        },
        {
          category: "Mental Rating",
          accuracy: 0,
          sampleSize: 0,
          confidence: 0,
        },
        {
          category: "Overall Rating",
          accuracy: 0,
          sampleSize: 0,
          confidence: 0,
        },
      ];

      if (Object.keys(categoryStats).length === 0) {
        this.predictionMetrics = defaultMetrics;
        return;
      }

      // Map category stats to prediction metrics
      this.predictionMetrics = [
        {
          category: "Technical Rating",
          accuracy: categoryStats["Skill"]
            ? (categoryStats["Skill"].average * 10).toFixed(1)
            : 0,
          sampleSize: categoryStats["Skill"] ? categoryStats["Skill"].count : 0,
          confidence: this.calculateConfidence(categoryStats["Skill"]),
        },
        {
          category: "Physical Rating",
          accuracy: categoryStats["Physical"]
            ? (categoryStats["Physical"].average * 10).toFixed(1)
            : 0,
          sampleSize: categoryStats["Physical"]
            ? categoryStats["Physical"].count
            : 0,
          confidence: this.calculateConfidence(categoryStats["Physical"]),
        },
        {
          category: "Mental Rating",
          accuracy: categoryStats["Mental"]
            ? (categoryStats["Mental"].average * 10).toFixed(1)
            : 0,
          sampleSize: categoryStats["Mental"]
            ? categoryStats["Mental"].count
            : 0,
          confidence: this.calculateConfidence(categoryStats["Mental"]),
        },
        {
          category: "Overall Rating",
          accuracy: parseFloat(this.overallAccuracy),
          sampleSize: this.sampleSize,
          confidence: this.calculateConfidence({ count: this.sampleSize }),
        },
      ];
    },

    // Calculate confidence interval based on sample size
    calculateConfidence(categoryData) {
      if (!categoryData || !categoryData.count || categoryData.count < 10)
        return 0;

      // Simplified confidence calculation based on sample size
      const baseConfidence = 10; // Base confidence interval
      const sampleSizeFactor = Math.sqrt(categoryData.count / 100); // Factor based on sample size
      return Math.max(1, baseConfidence / sampleSizeFactor).toFixed(1);
    },

    // Calculate category distributions from real assessment data
    calculateCategoryDistributions() {
      if (!this.allAssessmentsData.length) {
        // Return default values when no data available
        return {
          all: { mean: 0, stdDev: 0, label: "All Categories" },
          technical: { mean: 0, stdDev: 0, label: "Technical Rating" },
          physical: { mean: 0, stdDev: 0, label: "Physical Rating" },
          mental: { mean: 0, stdDev: 0, label: "Mental Rating" },
          overall: { mean: 0, stdDev: 0, label: "Overall Rating" },
        };
      }

      // Calculate statistics for each category from real data
      const categoryMappings = {
        technical: "skill",
        physical: "physique",
        mental: "mentality",
      };

      const distributions = {};
      const allRatings = [];

      // Calculate individual category distributions
      Object.keys(categoryMappings).forEach((categoryKey) => {
        const fieldName = categoryMappings[categoryKey];
        const ratings = [];

        this.allAssessmentsData.forEach((assessment) => {
          if (assessment[fieldName] && !isNaN(assessment[fieldName])) {
            const rating = parseFloat(assessment[fieldName]);
            ratings.push(rating);
            allRatings.push(rating);
          }
        });

        if (ratings.length > 0) {
          const mean =
            ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
          const variance =
            ratings.reduce(
              (sum, rating) => sum + Math.pow(rating - mean, 2),
              0,
            ) / ratings.length;
          const stdDev = Math.sqrt(variance);

          distributions[categoryKey] = {
            mean: mean,
            stdDev: Math.max(0.5, stdDev), // Minimum stdDev for chart display
            label:
              categoryKey.charAt(0).toUpperCase() +
              categoryKey.slice(1) +
              " Rating",
          };
        } else {
          distributions[categoryKey] = {
            mean: 0,
            stdDev: 0.5,
            label:
              categoryKey.charAt(0).toUpperCase() +
              categoryKey.slice(1) +
              " Rating",
          };
        }
      });

      // Calculate overall distribution from all ratings
      if (allRatings.length > 0) {
        const overallMean =
          allRatings.reduce((sum, rating) => sum + rating, 0) /
          allRatings.length;
        const overallVariance =
          allRatings.reduce(
            (sum, rating) => sum + Math.pow(rating - overallMean, 2),
            0,
          ) / allRatings.length;
        const overallStdDev = Math.sqrt(overallVariance);

        distributions.overall = {
          mean: overallMean,
          stdDev: Math.max(0.5, overallStdDev),
          label: "Overall Rating",
        };

        distributions.all = {
          mean: overallMean,
          stdDev: Math.max(0.5, overallStdDev),
          label: "All Categories",
        };
      } else {
        distributions.overall = {
          mean: 0,
          stdDev: 0.5,
          label: "Overall Rating",
        };
        distributions.all = { mean: 0, stdDev: 0.5, label: "All Categories" };
      }

      return distributions;
    },

    createDistributionChart() {
      const ctx = this.$refs.distributionChart?.getContext("2d");
      if (!ctx) return;

      const data = this.distributionData;

      this.distributionChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: data.labels,
          datasets: [
            {
              label: data.label,
              data: data.values,
              backgroundColor: "rgba(54, 162, 235, 0.6)",
              borderColor: "rgba(54, 162, 235, 1)",
              borderWidth: 1,
              borderRadius: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `${data.label} Distribution`,
              font: { size: 14, weight: "bold" },
            },
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const percentage = ((context.raw / data.total) * 100).toFixed(
                    1,
                  );
                  return `${context.raw} players (${percentage}%)`;
                },
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: "Number of Players",
              },
            },
            x: {
              title: {
                display: true,
                text: "Rating Range",
              },
            },
          },
          onClick: (event, elements) => {
            if (elements.length > 0) {
              const dataIndex = elements[0].index;
              this.$emit("data-point-click", {
                type: "distribution",
                category: this.selectedCategory,
                range: data.labels[dataIndex],
                count: data.values[dataIndex],
              });
            }
          },
        },
      });
    },

    updateDistributionChart() {
      // Prevent concurrent updates
      if (this._updating) {
        console.log("Update already in progress, skipping...");
        return;
      }

      // Clear any pending update
      if (this._chartUpdateTimeout) {
        clearTimeout(this._chartUpdateTimeout);
      }

      // Debounce the actual update
      this._chartUpdateTimeout = setTimeout(() => {
        this._performChartUpdate();
      }, 100);
    },

    _performChartUpdate() {
      console.log(
        "_performChartUpdate called for category:",
        this.selectedCategory,
      );

      if (this._updating) {
        console.log("Update flag is set, aborting");
        return;
      }

      if (!this.distributionChart) {
        console.error("Distribution chart not available");
        return;
      }

      this._updating = true;

      try {
        const data = this.distributionData;
        console.log("New chart data:", data);

        // Halt the old chart's animator before destroy, otherwise its next
        // tick fires after the canvas has been re-bound to the new chart and
        // crashes Chart.js inside clearCanvas.
        try { this.distributionChart.stop(); } catch (e) { /* noop */ }
        this.distributionChart.destroy();
        this.distributionChart = null;
        this.createDistributionChart();

        console.log("Chart recreated successfully");
      } catch (error) {
        console.error("Error updating distribution chart:", error);
      } finally {
        this._updating = false;
      }
    },

    handleCategoryChange() {
      console.log("Category changed to:", this.selectedCategory);
      // The watcher will handle the update, no need to call updateDistributionChart here
    },

    updateChartsData() {
      // Only update charts if they exist and prevent recursive calls
      if (this._updating) return;
      this._updating = true;

      try {
        this.updateDistributionChart();
      } catch (error) {
        console.error("Error updating charts:", error);
      } finally {
        this._updating = false;
      }
    },

    generateNormalDistribution(mean, stdDev, label) {
      const bins = [
        "1-2",
        "2-3",
        "3-4",
        "4-5",
        "5-6",
        "6-7",
        "7-8",
        "8-9",
        "9-10",
      ];
      const values = [];
      let total = 0;

      // Generate approximate normal distribution
      for (let i = 0; i < bins.length; i++) {
        const x = i + 1.5; // midpoint of bin
        const value = Math.round(
          ((this.sampleSize *
            Math.exp(-0.5 * Math.pow((x - mean) / stdDev, 2))) /
            (stdDev * Math.sqrt(2 * Math.PI))) *
          stdDev *
          2.5, // scaling factor
        );
        values.push(Math.max(0, value));
        total += values[i];
      }

      return {
        labels: bins,
        values,
        total,
        label,
      };
    },

    getAccuracyClass(accuracy) {
      if (accuracy >= 80) return "excellent";
      if (accuracy >= 70) return "good";
      if (accuracy >= 60) return "Progressing well";
      return "Unsatisfactory";
    },

    navigateBack() {
      this.$emit("navigate-back");
    },
  },
};
</script>

<style scoped>
/* Navigation Header Styles */
.analytics-nav-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  border-left: 4px solid #007bff;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-weight: 500;
}

.breadcrumb-item:hover {
  background: #e3f2fd;
  color: #0056b3;
}

.breadcrumb-item i {
  font-size: 1rem;
}

.breadcrumb-separator {
  color: #6c757d;
  font-size: 0.75rem;
}

.breadcrumb-current {
  color: #495057;
  font-weight: 600;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
}

.back-button:hover {
  background: #0056b3;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
}

.back-button i {
  font-size: 1rem;
}

.performance-benchmarking {
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.section-header {
  text-align: center;
  margin-bottom: 2rem;
}

.section-header h3 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.section-header p {
  color: #6c757d;
  margin: 0;
}

/* Benchmark Cards */
.benchmark-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.benchmark-card {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 1rem;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.benchmark-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.benchmark-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
}

.benchmark-icon.excellent {
  background: linear-gradient(135deg, #28a745, #20c997);
}

.benchmark-icon.good {
  background: linear-gradient(135deg, #17a2b8, #6f42c1);
}

.benchmark-icon.warning {
  background: linear-gradient(135deg, #ffc107, #fd7e14);
}

.benchmark-icon.info {
  background: linear-gradient(135deg, #007bff, #6610f2);
}

.benchmark-content h4 {
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  color: #2c3e50;
}

.benchmark-content p {
  margin: 0 0 0.5rem 0;
  color: #495057;
  font-weight: 500;
}

.benchmark-trend {
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  display: inline-block;
}

.benchmark-trend.trend-positive {
  background: #d4edda;
  color: #155724;
}

.benchmark-detail {
  font-size: 0.85rem;
  color: #6c757d;
  font-weight: 500;
}

/* Analytics Grid */
.analytics-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 2rem;
}

@media (max-width: 1200px) {
  .analytics-grid {
    grid-template-columns: 1fr;
  }
}

.chart-container {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.chart-container.full-width {
  grid-column: 1 / -1;
}

@media (max-width: 1200px) {
  .chart-container.full-width {
    grid-column: auto;
  }
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.chart-header h4 {
  color: #2c3e50;
  margin: 0;
  font-weight: 600;
}

.chart-subtitle {
  color: #6c757d;
  font-size: 0.9rem;
  margin: 0.25rem 0 0 0;
}

.chart-controls .form-select {
  min-width: 150px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
}

.chart-placeholder {
  position: relative;
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
}

.chart-container.full-width .chart-placeholder {
  height: 400px;
}

/* Prediction Metrics */
.prediction-metrics {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.metric-item {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #dee2e6;
  transition: border-color 0.2s ease;
}

.metric-item:hover {
  border-left-color: #007bff;
}

.metric-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.metric-name {
  font-weight: 600;
  color: #2c3e50;
}

.metric-score {
  font-weight: 700;
  font-size: 1.1rem;
}

.metric-score.excellent {
  color: #28a745;
}

.metric-score.good {
  color: #17a2b8;
}

.metric-score.average {
  color: #ffc107;
}

.metric-score.poor {
  color: #dc3545;
}

.metric-bar {
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.metric-fill {
  height: 100%;
  transition: width 0.3s ease;
  border-radius: 4px;
}

.metric-fill.excellent {
  background: linear-gradient(90deg, #28a745, #20c997);
}

.metric-fill.good {
  background: linear-gradient(90deg, #17a2b8, #6f42c1);
}

.metric-fill.average {
  background: linear-gradient(90deg, #ffc107, #fd7e14);
}

.metric-fill.poor {
  background: linear-gradient(90deg, #dc3545, #e83e8c);
}

.metric-details {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #6c757d;
}

.confidence {
  font-style: italic;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .performance-benchmarking {
    padding: 1rem;
  }

  .benchmark-cards {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .analytics-grid {
    gap: 1rem;
  }

  .chart-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .metric-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .metric-details {
    flex-direction: column;
    gap: 0.25rem;
  }

  .analytics-nav-header {
    flex-direction: column;
    gap: 1rem;
  }

  .breadcrumb {
    justify-content: center;
  }
}
</style>
